import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    DATEPICKER_PORTAL_ID,
    formatDateValue,
    parseDateValue,
} from "./document-body-utils";
import {DOCUMENT_DATE_PICKER_POPPER_Z_INDEX_CLASS} from "../constants";

const ReadOnlyDateDisplay = ({value}) => {
    const parsed = parseDateValue(value);
    const displayValue = parsed ? formatDateValue(parsed) : (value ?? "");
    return (
        <span className="block truncate text-sm text-gray-900">
            {displayValue}
        </span>
    );
};

const EditableDateInput = ({value, onCommit}) => {
    const initialValueRef = useRef(value ?? "");
    const [selected, setSelected] = useState(() => parseDateValue(value));

    useEffect(() => {
        if (typeof document === "undefined") {
            return undefined;
        }
        let portal = document.getElementById(DATEPICKER_PORTAL_ID);
        if (!portal) {
            portal = document.createElement("div");
            portal.id = DATEPICKER_PORTAL_ID;
            document.body.appendChild(portal);
        }

        return () => {
            // keep portal for reuse; do not remove to avoid thrashing
        };
    }, []);

    useEffect(() => {
        if (value === initialValueRef.current) {
            return;
        }
        initialValueRef.current = value ?? "";
        setSelected(parseDateValue(value));
    }, [value]);

    const handleChange = useCallback((date) => {
        const formatted = formatDateValue(date);
        setSelected(date ?? null);
        if (formatted === initialValueRef.current) {
            return;
        }
        initialValueRef.current = formatted;
        if (typeof onCommit === "function") {
            onCommit(formatted);
        }
    }, [onCommit]);

    return (
        <DatePicker
            selected={selected}
            onChange={handleChange}
            isClearable
            placeholderText=""
            portalId={DATEPICKER_PORTAL_ID}
            wrapperClassName="w-full"
            className="w-full bg-transparent text-sm text-gray-900 border-none focus:outline-none focus:ring-0"
            dateFormat="yyyy-MM-dd"
            popperClassName={DOCUMENT_DATE_PICKER_POPPER_Z_INDEX_CLASS}
        />
    );
};

const DocumentBodyEditableDateInput = ({isEditable, value, onCommit}) => (
    isEditable
        ? (
            <EditableDateInput
                value={value}
                onCommit={onCommit}
            />
        )
        : (
            <ReadOnlyDateDisplay value={value} />
        )
);

DocumentBodyEditableDateInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCommit: PropTypes.func,
    isEditable: PropTypes.bool,
};

DocumentBodyEditableDateInput.defaultProps = {
    value: "",
    onCommit: undefined,
    isEditable: true,
};

export default DocumentBodyEditableDateInput;
